import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, inr, apiError } from "@/lib/api";
import { LoginScreen, StatusBadge, Logo } from "@/components/shared";
import { toast } from "sonner";
import {
  Home, Wallet, User, Loader2, Camera, AlertTriangle,
  CheckCircle2, Flag, RefreshCw, Clock, Wifi, Bell, Smartphone,
} from "lucide-react";

const ACCENT = "#FFC400";
const TABS = [
  { key: "today", label: "Today", icon: Home, testid: "partner-bottom-nav-today" },
  { key: "cabnet", label: "CabNet", icon: Wifi, testid: "partner-bottom-nav-cabnet" },
  { key: "earnings", label: "Earnings", icon: Wallet, testid: "partner-bottom-nav-earnings" },
  { key: "profile", label: "Profile", icon: User, testid: "partner-bottom-nav-profile" },
];
const TAB_KEYS = TABS.map((t) => t.key);

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { screen } = useParams();
  const tab = TAB_KEYS.includes(screen) ? screen : "today";
  useEffect(() => {
    document.body.style.background = "#0C0A09";
    return () => { document.body.style.background = ""; };
  }, []);
  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  if (!user || user.portal !== "partner")
    return <LoginScreen portal="partner" isPhone demoCreds={[{ id: "9812340001", pw: "1234", label: "Driver" }]} />;

  return (
    <div className="min-h-screen bg-[#0C0A09]">
      <div className="relative mx-auto flex min-h-screen max-w-[460px] flex-col border-x border-slate-800 bg-slate-950 pb-20 shadow-2xl">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-4 py-3">
          <button onClick={() => navigate("/partner/today")} data-testid="logo-home-btn" className="tap flex cursor-pointer items-center gap-2.5 text-left"><Logo size={24} variant="white" /><span className="font-display text-base font-semibold text-white">Anand Partner</span></button>
          <button onClick={logout} data-testid="logout-btn" className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-300">Sign out</button>
        </header>
        <div className="flex-1 p-4">
          {tab === "today" && <Today />}
          {tab === "cabnet" && <CabNet />}
          {tab === "earnings" && <Earnings />}
          {tab === "profile" && <Profile />}
        </div>
        <nav className="fixed bottom-0 left-1/2 z-30 flex w-full max-w-[460px] -translate-x-1/2 border-t border-slate-800 bg-slate-950/95">
          {TABS.map((t) => {
            const on = tab === t.key;
            return (
              <button key={t.key} data-testid={t.testid} onClick={() => navigate(`/partner/${t.key}`)}
                className="tap relative flex flex-1 flex-col items-center gap-1 py-2.5 transition-colors" style={on ? { color: ACCENT } : { color: "#64748b" }}>
                {on && <span className="absolute top-0 h-[2.5px] w-8 rounded-b-full" style={{ background: ACCENT }} />}
                <t.icon size={20} strokeWidth={on ? 2.4 : 2} /><span className={`text-[10px] font-medium ${on ? "font-semibold" : ""}`}>{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

function useDriver() {
  const [d, setD] = useState(null);
  const reload = useCallback(() => http.get("/drivers/me").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return [d, reload];
}
const Loader = () => <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-500" /></div>;
const Card = ({ children, className = "", ...rest }) => <div className={`rounded-xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-900/70 p-4 shadow-[0_1px_3px_rgba(0,0,0,0.3)] ${className}`} {...rest}>{children}</div>;

// ---------------- Today ----------------
function Today() {
  const [d, reload] = useDriver();
  const [camOpen, setCamOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const { config } = useAuth();
  if (!d) return <Loader />;

  if (!d.cab_id) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div><div className="text-xs text-slate-500">Namaste</div><div className="font-display text-lg font-semibold text-white">{d.name}</div></div>
        </div>
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20"><Clock size={22} className="text-amber-400" /></div>
          <div className="font-display text-base font-semibold text-white">You're on the waitlist</div>
          <p className="mt-1.5 text-sm text-slate-300">Your partner account is ready. We'll assign you to a cab and get your ads playing once a slot opens up in <span className="font-semibold text-slate-200">{d.zone || "your area"}</span>.</p>
        </div>
        <Card>
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><CheckCircle2 size={16} className="text-emerald-400" /> What happens next</div>
          <ul className="mt-2.5 space-y-1.5 text-sm text-slate-400">
            <li>• Ops verifies your KYC and assigns your cab</li>
            <li>• You get paid ₹{(config?.rules?.DRIVER_PAY_PER_CAB_MONTH ?? 300).toLocaleString("en-IN")} per month per cab</li>
            <li>• Open this app daily to capture your compliance photo</li>
          </ul>
        </Card>
        <button onClick={() => setReportOpen(true)} className="tap flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:text-white"><Flag size={15} /> Report a problem</button>
        <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} />
      </div>
    );
  }

  const dueIn = d.photo_due_in;
  const overdue = dueIn < 0;
  const veryOverdue = dueIn < -3;

  return (
    <div className="space-y-4">
      <div><div className="text-xs text-slate-500">Namaste</div><div className="font-display text-lg font-semibold text-white">{d.name}</div></div>

      {(overdue || d.compliance === "overdue") ? (
        <ComplianceCard status="overdue" dueIn={dueIn} onSubmit={() => setCamOpen(true)} veryOverdue={veryOverdue} />
      ) : d.compliance === "submitted" ? (
        <ComplianceCard status="submitted" onSubmit={() => setCamOpen(true)} />
      ) : (
        <ComplianceCard status="ok" dueIn={dueIn} onSubmit={() => setCamOpen(true)} />
      )}

      <button onClick={() => setReportOpen(true)} className="tap flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:text-white"><Flag size={15} /> Report a problem</button>

      <CameraModal open={camOpen} onClose={() => setCamOpen(false)} onDone={reload} />
      <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}

function ComplianceCard({ status, dueIn, onSubmit, veryOverdue }) {
  const cfg = {
    overdue: { bg: "border-rose-500/40 bg-rose-500/10", icon: <AlertTriangle className="text-rose-400" />, title: "Compliance photo OVERDUE", text: `Was due ${Math.abs(dueIn)} day(s) ago. Submit now to avoid a hold.` },
    submitted: { bg: "border-amber-500/40 bg-amber-500/10", icon: <RefreshCw className="text-amber-400" />, title: "Photo under review", text: "Admin will verify shortly." },
    ok: { bg: "border-emerald-500/30 bg-emerald-500/10", icon: <CheckCircle2 className="text-emerald-400" />, title: "Compliance up to date", text: `Next photo due in ${dueIn} day(s).` },
  }[status];
  return (
    <div className={`rounded-lg border p-4 ${cfg.bg}`}>
      <div className="flex items-center gap-2">{cfg.icon}<span className="font-display text-sm font-semibold text-white">{cfg.title}</span></div>
      <p className="mt-1 text-sm text-slate-300">{cfg.text}</p>
      {veryOverdue && <p className="mt-1 text-xs font-semibold text-rose-300">Flagged to admin — 3+ days overdue.</p>}
      {status !== "submitted" && (
        <button data-testid="partner-compliance-photo-trigger" onClick={onSubmit}
          className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 py-3 font-semibold text-white transition-colors hover:bg-emerald-500">
          <Camera size={17} /> Submit compliance photo
        </button>
      )}
    </div>
  );
}

function CameraModal({ open, onClose, onDone }) {
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setImg(r.result); r.readAsDataURL(f); };
  const submit = async () => { setBusy(true); try { await http.post("/drivers/me/compliance-photo", { image: img }); toast.success("Submitted for review"); onDone(); setImg(null); onClose(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <Modal open={open} onClose={onClose} title="Compliance photo" testid="compliance-camera-modal">
      <p className="mb-3 text-sm text-slate-400">Photograph the tablet mounted behind the headrest with an ad playing.</p>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
      {!img ? (
        <button data-testid="partner-camera-capture-btn" onClick={() => fileRef.current.click()}
          className="tap flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-slate-600 text-slate-400 transition-colors hover:border-slate-500">
          <Camera size={44} /><span className="text-sm">Open camera / choose photo</span>
        </button>
      ) : (
        <div>
          <div className="relative aspect-[3/4] overflow-hidden rounded-lg border border-slate-700">
            <img src={img} alt="preview" className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-6 rounded-lg border-2 border-dashed border-white/50" />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => { setImg(null); fileRef.current.value = ""; }} className="tap flex-1 rounded-lg border border-slate-700 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600">Retake</button>
            <button data-testid="partner-compliance-submit-btn" onClick={submit} disabled={busy} className="tap flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-60">{busy && <Loader2 size={15} className="animate-spin" />} Confirm & submit</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const PROBLEMS = ["Tablet not turning on", "Screen cracked", "No internet / SIM issue", "Ad not playing", "Mount loose", "Other"];
function ReportModal({ open, onClose }) {
  const [sel, setSel] = useState(PROBLEMS[0]);
  const send = () => { toast.success("Reported to ops — someone will reach out"); onClose(); };
  return (
    <Modal open={open} onClose={onClose} title="Report a problem">
      <div className="space-y-2">{PROBLEMS.map((p) => <button key={p} onClick={() => setSel(p)} className={`tap block w-full rounded-lg border px-3 py-2.5 text-left text-sm ${sel === p ? "border-slate-600 bg-slate-800 text-white" : "border-slate-700 text-slate-300"}`} style={sel === p ? { borderColor: ACCENT } : {}}>{p}</button>)}</div>
      <button onClick={send} className="tap mt-4 w-full rounded-lg bg-emerald-600 py-3 font-semibold text-white transition-colors hover:bg-emerald-500">Send report</button>
    </Modal>
  );
}

function Modal({ open, onClose, children, title, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        {title && <h3 className="mb-4 font-display text-base font-semibold text-white">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

// ---------------- CabNet ----------------
function b64UrlToU8(s) {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
function bufToB64Url(buf) {
  const b = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
  return btoa(bin).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
}
function lastSeen(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString();
}

function isAndroidUA() { return /android/i.test(navigator.userAgent || ""); }
function isIOSUA() { return /iphone|ipad|ipod/i.test(navigator.userAgent || ""); }

function CabNet() {
  const { config } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [push, setPush] = useState("checking");
  const [hotspotOn, setHotspotOn] = useState(() => localStorage.getItem("cabnet_hotspot_today") === "1");

  const toggleHotspot = () => {
    const v = !hotspotOn;
    setHotspotOn(v);
    localStorage.setItem("cabnet_hotspot_today", v ? "1" : "0");
    if (!v) reload();
  };

  const showHotspotHelp = () => {
    toast.info("Hotspot is a phone setting — swipe down from the top of your phone and tap the Hotspot tile.", { duration: 6000 });
  };

  const openHotspotSettings = () => {
    if (!isAndroidUA() && !isIOSUA()) { showHotspotHelp(); return; }
    const link = isAndroidUA()
      ? "intent:#Intent;action=android.settings.TETHER_SETTINGS;end"
      : "App-Prefs:root=INTERNET_TETHERING";
    try {
      const a = document.createElement("a");
      a.href = link;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 0);
      // If the browser actually switched to settings, the page goes hidden and
      // we skip the fallback; if it stayed put (blocked/WebView), guide instead
      // of failing silently.
      let hidden = false;
      const onVis = () => { if (document.hidden) hidden = true; };
      document.addEventListener("visibilitychange", onVis);
      setTimeout(() => {
        document.removeEventListener("visibilitychange", onVis);
        if (!hidden) showHotspotHelp();
      }, 2000);
    } catch (e) { showHotspotHelp(); }
  };

  const reload = useCallback(() => {
    http.get("/cabnet/status").then((r) => setS(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reload(); const t = setInterval(reload, 45000); return () => clearInterval(t); }, [reload]);

  useEffect(() => {
    let dead = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) { if (!dead) setPush("unsupported"); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!dead) setPush(sub ? "on" : "off");
      } catch { if (!dead) setPush("off"); }
    })();
    return () => { dead = true; };
  }, []);

  const toggleAlert = async () => {
    setBusy(true);
    try {
      const pub = config?.vapid_public_key;
      if (!pub) { toast.info("Alerts aren't configured yet — try again in a few minutes."); return; }
      await navigator.serviceWorker.register("/sw.js");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { toast.error("Notifications are blocked in your browser settings."); return; }
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64UrlToU8(pub) });
        await http.post("/cabnet/subscribe", {
          endpoint: sub.endpoint,
          p256dh: bufToB64Url(sub.getKey("p256dh")),
          auth: bufToB64Url(sub.getKey("auth")),
        });
        setPush("on"); toast.success("Alerts on — we'll ping you when new ads are ready");
      } else {
        await http.post("/cabnet/unsubscribe");
        await sub.unsubscribe();
        setPush("off"); toast.success("Alerts off");
      }
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <Loader />;
  if (!s.linked)
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div><div className="text-xs text-slate-500">CabNet</div><div className="font-display text-lg font-semibold text-white">Screen sync</div></div>
          <Smartphone size={20} className="text-slate-600" />
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-800"><Wifi size={22} className="text-slate-400" /></div>
          <div className="font-display text-base font-semibold text-white">No cab linked to this account yet</div>
          <p className="mt-1.5 text-sm text-slate-400">Once ops assigns you a cab screen, this tab keeps it updated automatically each shift.</p>
        </div>
      </div>
    );

  const cabs = (s.cabs && s.cabs.length)
    ? s.cabs
    : (s.plate ? [{ plate: s.plate, pending: s.pending, last_checkin: s.last_checkin, battery: s.battery, app_version: s.app_version, playing: s.playing }] : []);
  const anyPending = cabs.some((c) => c.pending);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><div className="text-xs text-slate-500">CabNet</div><div className="font-display text-lg font-semibold text-white">Screen sync</div></div>
        <button onClick={reload} className="tap flex items-center gap-1.5 rounded-lg border border-slate-700 py-2 pl-2.5 pr-3 text-xs font-medium text-slate-300 transition-colors hover:border-slate-600 hover:text-white"><RefreshCw size={13} /> Check now</button>
      </div>

      <div className={`rounded-xl border p-4 ${hotspotOn ? "border-emerald-500/30 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hotspotOn ? <CheckCircle2 className="flex-none text-emerald-400" /> : <Wifi className="flex-none text-amber-400" />}
            <div>
              <div className="font-display text-sm font-semibold text-white">{hotspotOn ? "Hotspot on — screens stay updated" : "Hotspot off today"}</div>
              <div className="text-[11px] text-slate-500">Flip ON at the start of your shift, back off at the end</div>
            </div>
          </div>
          <button onClick={toggleHotspot} aria-label="Hotspot for today"
            className="tap flex h-7 w-12 flex-none items-center rounded-full p-1 transition-colors"
            style={hotspotOn ? { background: ACCENT, justifyContent: "flex-end" } : { background: "#1F2937", justifyContent: "flex-start" }}>
            <span className="h-5 w-5 rounded-full bg-white shadow" style={hotspotOn ? { marginLeft: 2 } : undefined} />
          </button>
        </div>
        <p className="mt-2 text-sm text-slate-300">
          {hotspotOn
            ? "Nothing to do the rest of the shift — the screens check and update themselves (about every 5 min). Switch back off when you wrap up."
            : anyPending
              ? "New ads are waiting — flip it on while you're in the cab and they'll sync in the background."
              : "No new ads right now. Keeping it on during your shift means real-time updates all day long."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={openHotspotSettings}
            className={`tap inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${hotspotOn ? "border border-slate-700 text-slate-300 hover:border-slate-600 hover:text-white" : "text-slate-900 hover:opacity-90"}`}
            style={!hotspotOn ? { background: ACCENT } : undefined}>
            <Wifi size={13} /> {hotspotOn ? "Open hotspot settings" : "Turn hotspot on"}
          </button>
          {!isAndroidUA() && !isIOSUA() && (
            <span className="text-[11px] text-slate-500">Hotspot is a phone setting — open this screen on your phone to reach it.</span>
          )}
        </div>
      </div>

      {cabs.map((cab) => {
        const pending = cab.pending;
        const bat = cab.battery !== null && cab.battery !== undefined ? `${cab.battery}%` : "—";
        return (
          <div key={cab.plate} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div className="font-display text-sm font-semibold text-white">{cab.plate}</div>
              {pending
                ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-400"><AlertTriangle size={12} /> {hotspotOn ? "queued — will sync on next check" : "waiting for hotspot"}</span>
                : <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-400"><CheckCircle2 size={12} /> Up to date</span>}
            </div>
            <div className="px-4 text-[11px] text-slate-500">last checked {lastSeen(cab.last_checkin)} · battery {bat} · player v{cab.app_version ?? "?"}</div>
            <div className="px-4 pb-4 pt-2">
              <h4 className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-600">On your screen right now</h4>
              <div className="space-y-2">
                {(cab.playing || []).map((ad) => (
                  <div key={ad.slot} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-slate-800 font-mono text-[11px] text-slate-400">{ad.slot}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-white">{ad.title}</div>
                      <div className="truncate text-[11px] text-slate-500">{ad.advertiser_name} · {(ad.duration || 15).toFixed(1)}s</div>
                    </div>
                  </div>
                ))}
                {cab.playing?.length === 0 && <div className="py-3 text-center text-sm text-slate-500">No ads assigned to this screen yet.</div>}
              </div>
            </div>
          </div>
        );
      })}

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold text-white">Optional alerts</div>
            <div className="text-[11px] text-slate-500">Ping me if the screen has something new to sync mid-shift</div>
          </div>
          <button onClick={toggleAlert} disabled={busy || push === "unsupported"}
            className={`tap flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${push === "on" ? "" : "bg-slate-800 text-slate-300 hover:text-white"}`}
            style={push === "on" ? { background: ACCENT, color: "#1F2937" } : undefined}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
            {busy ? "Working…" : push === "on" ? "Alerts on" : push === "unsupported" ? "Not supported" : "Turn on"}
          </button>
        </div>
        {push === "checking" && <div className="mt-2 text-[11px] text-slate-500">Checking notification support…</div>}
      </Card>

      <div className="rounded-lg border border-dashed p-4" style={{ borderColor: ACCENT + "66" }}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Wifi size={13} /> How CabNet keeps your screen fresh</div>
        <ul className="mt-2 space-y-1.5 text-sm text-slate-400">
          <li>• Your cab screen is Wi-Fi-only — it stays connected while your phone's hotspot is on.</li>
          <li>• Flip "Hotspot on today" at the start of your shift and forget about it.</li>
          <li>• Updates are tiny, about 5 MB each — nothing like streaming a video.</li>
          <li>• No data used in between; the screen plays the latest ads offline.</li>
        </ul>
      </div>
    </div>
  );
}

// ---------------- Earnings ----------------
function Earnings() {
  const { config } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => { http.get("/payouts/me").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return <Loader />;
  const week = [["Mon", 60], ["Tue", 60], ["Wed", 45], ["Thu", 60], ["Fri", 60], ["Sat", 30], ["Sun", 0]];
  return (
    <div className="space-y-4">
      <Card>
        <div className="text-xs uppercase tracking-wide text-slate-500">Next payout · {p.next_date}th</div>
        <div className="mt-1 font-display text-3xl font-bold tabular-nums text-white">{inr(p.estimated)}</div>
        <div className="mt-1 text-xs text-slate-500">₹{config?.rules?.DRIVER_PAY_PER_CAB_MONTH ?? 300} per cab · paid on 1st & 16th</div>
      </Card>
      <Card>
        <h3 className="mb-3 font-display text-sm font-semibold text-white">This week</h3>
        <div className="flex items-end justify-between gap-2" style={{ height: 100 }}>
          {week.map(([d, v]) => <div key={d} className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-t-md transition-all" style={{ height: `${(v / 60) * 80 + 4}px`, background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT}66)`, opacity: v ? 1 : 0.3 }} /><span className="text-[10px] text-slate-500">{d}</span></div>)}
        </div>
      </Card>
      <Card>
        <h3 className="mb-3 font-display text-sm font-semibold text-white">Payout history</h3>
        {p.history.length === 0 ? <div className="py-4 text-center text-sm text-slate-500">No payouts yet.</div> :
          p.history.map((h) => <div key={h.id} className="flex items-center justify-between border-b border-slate-800 py-2 last:border-0"><div><div className="text-sm text-white">{h.period}</div><div className="font-mono text-[10px] text-slate-500">{h.utr}</div></div><div className="font-mono font-bold text-emerald-400">{inr(h.amount)}</div></div>)}
      </Card>
    </div>
  );
}

// ---------------- Profile ----------------
function Profile() {
  const [d] = useDriver();
  if (!d) return <Loader />;
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center py-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full" style={{ background: ACCENT }}><Logo size={44} variant="white" /></div>
        <div className="mt-2 font-display text-lg font-semibold text-white">{d.name}</div>
        <div className="text-sm text-slate-500">{d.phone}</div>
        <div className="mt-1"><StatusBadge status={d.kyc_status === "verified" ? "verified" : "pending"} /></div>
      </div>
      <Card>
        <Row l="Cab plate" v={d.plate} /><Row l="Payout UPI" v={d.payout_upi} />
      </Card>
      <Card>
        <h3 className="mb-2 font-display text-sm font-semibold text-white">Rules of the road</h3>
        <ul className="space-y-1.5 text-sm text-slate-400"><li>• Keep the tablet powered and mounted at all times.</li><li>• Submit the compliance photo every 15 days.</li><li>• Don't cover or turn off the screen during shifts.</li><li>• Report faults immediately from the Today tab.</li></ul>
      </Card>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between border-b border-slate-800 py-2 text-sm last:border-0"><span className="text-slate-400">{l}</span><span className="font-mono text-white">{v}</span></div>;
